import React, { useEffect, useState } from 'react';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { PreviewFrame } from './PreviewFrame';

// Salesforce generates the PDF rendition of a Word file lazily, on the first
// request for it, and answers 202 until it is ready. That is a one-off wait per
// document, so the viewer polls rather than giving up — reporting "no preview"
// for a document that is merely still rendering would send the reader off to
// download it, which is the thing this preview exists to avoid.
// Every poll costs a Salesforce API call, so the waits lengthen instead of
// staying fixed: an org with thousands of Word files would otherwise spend a
// large slice of its daily API allowance on the first view of each one. The
// first wait is short because an already-rendered document answers immediately.
const RENDITION_PENDING_STATUS = 202;
const POLL_DELAYS_MS = [2000, 3000, 4500, 6750, 10000, 10000, 10000, 10000];

const i18Prefix = 'documents2.documentPreview';

type Status = 'checking' | 'preparing' | 'ready' | 'unavailable';

const isPdf = (response: Response): boolean =>
  !!response.headers?.get('content-type')?.includes('application/pdf');

type Props = {
  url: string;
  width: string | number;
  // Called once the skeleton must come down, whether that ends in a rendered
  // preview or in a message shown in its place.
  onLoad: () => void;
};

export const WordDocumentPreview: React.FC<Props> = ({
  url,
  width,
  onLoad,
}) => {
  const t = useNamespacedTranslation(i18Prefix);
  const [status, setStatus] = useState<Status>('checking');

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      let response: Response;
      try {
        // 'reload' asks the network every time — a document that failed once
        // must not stay unavailable because the failure is sitting in a cache —
        // while still storing a successful response for the frame below to use.
        response = await fetch(url, { cache: 'reload' });
        // Only the status is needed here; the frame below re-requests the URL to
        // display it. Without this the abandoned body keeps streaming, holding
        // the server's response — and the Salesforce connection behind it — open
        // until the browser collects it.
        await response.body?.cancel();
      } catch {
        if (!cancelled) {
          setStatus('unavailable');
        }
        return;
      }
      if (cancelled) {
        return;
      }
      if (response.status !== RENDITION_PENDING_STATUS) {
        // A successful status is not enough to conclude the rendition is there:
        // CloudFront turns the origin's 404 into 200 plus the single-page app's
        // own index.html, which would otherwise be mounted in the frame as if it
        // were the document. Only a PDF is a preview.
        setStatus(response.ok && isPdf(response) ? 'ready' : 'unavailable');
        return;
      }
      const delay = POLL_DELAYS_MS[attempts];
      if (delay === undefined) {
        setStatus('unavailable');
        return;
      }
      attempts += 1;
      setStatus('preparing');
      timer = setTimeout(check, delay);
    };

    setStatus('checking');
    check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [url]);

  useEffect(() => {
    if (status === 'preparing' || status === 'unavailable') {
      onLoad();
    }
  }, [status, onLoad]);

  if (status === 'checking') {
    return null;
  }

  if (status === 'ready') {
    return <PreviewFrame url={url} width={width} onLoad={onLoad} />;
  }

  return (
    <div className="d-flex h-100 align-items-center justify-content-center text-muted px-4 text-center">
      {status === 'preparing' ? t('preparingPreview') : t('previewUnavailable')}
    </div>
  );
};
